import { ModelType, SubscriptionCardType } from '../../common/enums.js';
import {
  getAddonsRecurringPrice,
  getM2MOpeningFee,
  getSelfServiceSubscriptionPrice,
  isMobileBroadbandModel,
} from '../../common/utils/addOnUtils.js';
import { getOfferCampaignAssociationAndPromotion } from '../../common/utils/campaignUtils.js';
import { getCommercialProductPrices } from '../../common/utils/commercialProductUtils.js';
import { isCommitmentPeriodActive } from '../../common/utils/subscriptionUtils.js';
import { SalesType } from '../../generated/api/salesType.js';
import type { CampaignContextsFromVoucher } from './CampaignProductCard.js';
import type { CampaignAssociation } from '../../generated/api/campaignAssociation.js';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion.js';
import type { CommercialProduct } from '../../generated/api/commercialProduct.js';
import type { Offer } from '../../generated/api/offer.js';
import type { OnlineModel } from '../../generated/api/onlineModel.js';
import type { Subscription } from '../../generated/api/subscription.js';
import type { SubCardWithAnalyticsV4Props } from '../HorizontalSubscriptionCard/subscriptionCardsAnalyticsUtils.js';
import type { SubscriptionCardProps } from '../SubscriptionCard/SubscriptionCard.js';

export const isChangeOfferAllowed = (
  isChangeOffer: boolean,
  currentCP: CommercialProduct,
  subscription?: Subscription,
  campaignAssociation?: CampaignAssociation
) => {
  // In case of doing a change offer from fixed term campaigns we need to ensure that
  // 1) It's only possible to update to other fixed term campaign
  // 2) It's not possible to update to a cheaper offer
  if (isChangeOffer && isCommitmentPeriodActive(subscription?.details?.commitmentEndDate)) {
    if (!campaignAssociation?.fixedTermPeriod) {
      return false;
    }

    const discountedPrice = getCommercialProductPrices(currentCP, campaignAssociation, SalesType.CHANGE_OFFER);

    if ((subscription?.details?.monthlyRecurringCharge || 0) > (discountedPrice?.monthlyRecurringCharge || 0)) {
      return false;
    }
  }
  return true;
};

export type OfferPerGroup = { groupName: string; offerCode: string; addonCode?: string };

// Should never happen, if offer/onlineModel is missing in this point there are some serious issues
export const validateOfferAndModel = (offer?: Offer, onlineModel?: OnlineModel): void => {
  if (!offer) {
    throw new Error('Cannot create subscription card content, offer missing');
  } else if (!onlineModel) {
    throw new Error('Cannot create subscription card content, online model missing');
  }
};

const hasMatchingOffer = (onlineModelOfferCodes: string[], cardContent: SubscriptionCardProps) => {
  const offerCodes = cardContent.offers?.map(offer => offer.guid) || [];

  switch (cardContent.cardType) {
    case SubscriptionCardType.VOICE:
      return offerCodes.some(offerCode => onlineModelOfferCodes.includes(offerCode));
    case SubscriptionCardType.MOBILE_M2M:
    case SubscriptionCardType.MOBILE_BROADBAND:
      return onlineModelOfferCodes.includes(cardContent.selectedOffer || '');
    default:
      return false;
  }
};

const isEligibleOffer = (offer: Offer, cardContents: SubscriptionCardProps[]): boolean =>
  cardContents.some(
    content =>
      (content.offers && content.offers.some(off => off.guid === offer.offerCode)) ||
      content.selectedOffer === offer.offerCode
  );

const getNonNegativePrice = (price?: number) => (!price || price < 0 ? 0 : price);

interface EffectivePriceParams {
  /** Price before any campaign discounts, used as the base for the voucher discount. */
  priceBeforeDiscounts: number;
  /** Voucher discount amount, or 0 when no voucher discount applies. */
  voucherDiscount: number;
  /** SELF_SERVICE addon price, or undefined when SELF_SERVICE pricing does not apply to this card. */
  selfServicePrice?: number;
  /** Commercial product price, used when neither a voucher nor SELF_SERVICE pricing applies. */
  commercialProductPrice: number;
}

/**
 * Resolves the price shown on a subscription card, in priority order:
 * 1. Voucher campaign - it replaces rather than stacks with the default product campaign, so the
 *    discount is applied to the price before discounts.
 * 2. SELF_SERVICE addon pricing, which already includes the default product campaign.
 * 3. Commercial product pricing.
 */
const resolveEffectivePrice = ({
  priceBeforeDiscounts,
  voucherDiscount,
  selfServicePrice,
  commercialProductPrice,
}: EffectivePriceParams): number => {
  if (voucherDiscount) {
    return getNonNegativePrice(priceBeforeDiscounts - voucherDiscount);
  }
  if (selfServicePrice !== undefined) {
    return getNonNegativePrice(selfServicePrice);
  }
  return getNonNegativePrice(commercialProductPrice);
};

/** Extracts pricing for product cards. Campaign/voucher pricing is applied via getCommercialProductPrices. */
export const getOfferPricing = (cp: CommercialProduct, campaignAssociation?: CampaignAssociation) =>
  getCommercialProductPrices(cp, campaignAssociation, campaignAssociation?.salesType);

/**
 * Extracts fixed term period (in months) from MBB campaign name or description.
 * For public API MBB products, fixedTermPeriod is not in campaignAssociation but encoded in the campaign name/description.
 * Example name: "2025 Yritysdata 5G+ (1000M) 24 09" -> 24 months
 * Example description: "...24 kk määräaikaisuus" -> 24 months
 */
export const getInternetAddonCode = (offer: Offer) => {
  // For Laitenetti (MobileM2M), getOnlineModel creates synthetic offers with a mandatory Internet addon
  // that distinguishes S/M/L variants. We must use the mandatory addon, not the first Internet addon.
  const mandatoryInternetAddon = offer.commercialProducts[0]?.addOnAssociations?.find(
    assoc => assoc.addOnPurpose === 'MANDATORY'
  )?.addOnCode;

  if (mandatoryInternetAddon) {
    return mandatoryInternetAddon;
  }

  // Fallback to first Internet addon for offers without mandatory addons
  return offer.commercialProducts[0].associatedAddOns?.find(
    associatedAddOn => associatedAddOn.addOnGroup === 'Internet'
  )?.addOnCode;
};

export const getMandatoryAddonPrice = (commercialProduct: CommercialProduct): number => {
  // For Laitenetti (MobileM2M), only the mandatory addon price should be included
  const mandatoryAddonCode = commercialProduct.addOnAssociations?.find(
    assoc => assoc.addOnPurpose === 'MANDATORY'
  )?.addOnCode;

  if (!mandatoryAddonCode) {
    return 0;
  }

  const mandatoryAddon = commercialProduct.associatedAddOns?.find(addon => addon.addOnCode === mandatoryAddonCode);

  return mandatoryAddon?.price?.effectivePrice.monthlyRecurringCharge ?? 0;
};

export const isOfferAvailable = (offer: Offer, subscription?: Subscription, onlineModelCode?: string) => {
  // For Laitenetti we need to check addons, because every Laitenetti type (S, M, L) has the same Offer
  if (onlineModelCode === ModelType.MobileM2M) {
    const addonCode = getInternetAddonCode(offer);
    return !subscription?.details?.selectedAddOns?.find(addon => addon.addOnCode === addonCode);
  }
  return offer.commercialProducts[0].commercialProductCode !== subscription?.commercialProductCode;
};

export const getSubscriptionCardContent = (
  isChangeOffer: boolean,
  campaignContextsFromVoucher: CampaignContextsFromVoucher,
  campaignPromotions: CampaignPromotion[],
  availableContent: SubscriptionCardProps[],
  selectedOffersPerGroup: OfferPerGroup[],
  setSelectedOffersPerGroup: (offerPerGroup: OfferPerGroup[]) => void,
  selectedOffer?: Offer,
  onlineModel?: OnlineModel,
  subscription?: Subscription,
  additionalOffers?: Offer[]
): SubCardWithAnalyticsV4Props[] => {
  const baseOffers = onlineModel?.offers.concat(additionalOffers || []) || [];

  const eligibleOfferDetails = baseOffers
    .filter(offer => isOfferAvailable(offer, subscription, onlineModel?.onlineModelCode))
    .map(offer => {
      const result = getOfferCampaignAssociationAndPromotion(
        offer,
        isChangeOffer ? SalesType.CHANGE_OFFER : SalesType.NEW_SALE,
        campaignContextsFromVoucher.campaignContexts,
        campaignContextsFromVoucher.voucher,
        campaignPromotions
      );

      const { campaignAssociation, campaignPromotion, voucher } = result || {};

      const addOnCode = getInternetAddonCode(offer);

      return {
        campaignAssociation,
        campaignPromotion,
        offer,
        voucherInUse: voucher !== undefined,
        voucher,
        addOnCode,
      };
    })
    .filter(({ offer }) => isEligibleOffer(offer, availableContent))
    .filter(({ offer, campaignAssociation }) =>
      isChangeOfferAllowed(isChangeOffer, offer.commercialProducts[0], subscription, campaignAssociation)
    );

  const eligibleOffers = eligibleOfferDetails.map(offer => offer.offer);
  const eligibleOfferCodes = eligibleOffers.map(offer => offer.offerCode);

  const handleChangeOffer = (guid: string, groupName?: string) =>
    setSelectedOffersPerGroup([
      ...selectedOffersPerGroup.filter(off => off.groupName !== groupName),
      { groupName: groupName || '', offerCode: guid },
    ]);

  const findSelectedOfferByGroup = (groupName: string) =>
    selectedOffersPerGroup.find(off => off.groupName === groupName)?.offerCode || '';

  return availableContent
    .filter(cardContent =>
      // hide Subscription Cards when none of its offers are eligible
      hasMatchingOffer(eligibleOfferCodes, cardContent)
    )
    .map(cardContent => {
      // Update default selected offer for current card if the pre-selected offer is not eligible
      // e.g if the offer being changed is default selected then change selected to first available offer
      const availableOfferButtons = cardContent.offers?.filter(offer => eligibleOfferCodes.includes(offer.guid));
      if (!eligibleOfferCodes.includes(findSelectedOfferByGroup(cardContent.cardName || ''))) {
        handleChangeOffer(availableOfferButtons?.[0]?.guid || '', cardContent.cardName);
      }
      return cardContent;
    })
    .reduce<SubCardWithAnalyticsV4Props[]>((acc, cardContent) => {
      const selectedGuidForGroup = selectedOffersPerGroup.find(off => off.groupName === cardContent.cardName);
      const selectedGuidForGroupCode = selectedGuidForGroup?.addonCode || selectedGuidForGroup?.offerCode || '';

      const selectedOfferDetails = eligibleOfferDetails.find(
        details =>
          details.offer.offerCode === selectedGuidForGroupCode || details.addOnCode === selectedGuidForGroupCode
      );

      if (selectedOfferDetails) {
        const cp = selectedOfferDetails.offer.commercialProducts[0];
        const selfServicePricing = onlineModel ? getSelfServiceSubscriptionPrice(cp, onlineModel) : undefined;
        const campaignAssociation = selectedOfferDetails.campaignAssociation ?? selfServicePricing?.campaignAssociation;
        const cpDirectMrc = cp.price?.effectivePrice?.monthlyRecurringCharge || cp.monthlyRecurringCharge || 0;

        // For MBB cards we should only use SELF_SERVICE addon pricing when CP base MRC is zero.
        // If CP already has MRC (e.g. Liikkuva WiFi/sales-product style offers), use CP campaign pricing.
        const applicableSelfServicePricing =
          selfServicePricing && (!onlineModel || !isMobileBroadbandModel(onlineModel) || cpDirectMrc === 0)
            ? selfServicePricing
            : undefined;

        // Addon price calculation:
        // - M2M: only the mandatory addon price (S/M/L variant)
        // - Other: sum all associated addons
        const addonsRecurringPrice =
          cardContent.cardType === SubscriptionCardType.MOBILE_M2M
            ? getMandatoryAddonPrice(cp)
            : getAddonsRecurringPrice(cp, false);

        const prices = getOfferPricing(cp, selectedOfferDetails.campaignAssociation);

        const currentCardSelectedOffer = selectedOffersPerGroup.find(off => off.groupName === cardContent.cardName);

        // Opening fee extraction:
        // - M2M: use shared utility (handles public vs private model differences)
        // - Other: from CP prices
        const oneTimeCharge =
          cardContent.cardType === SubscriptionCardType.MOBILE_M2M && onlineModel
            ? getM2MOpeningFee(prices.oneTimeCharge, onlineModel)
            : (prices.oneTimeCharge ?? 0);

        const originalMonthlyPrice = applicableSelfServicePricing
          ? applicableSelfServicePricing.originalMonthlyRecurringCharge
          : (prices.monthlyRecurringChargeBeforeDiscount ?? 0) + addonsRecurringPrice;
        const originalOneTimePrice = applicableSelfServicePricing
          ? applicableSelfServicePricing.originalOneTimeCharge
          : prices.oneTimeChargeBeforeDiscount;

        // Voucher API returns the discount in the campaignAssociation
        // (CP-level), NOT in the addon/CP effectivePrice. Self service pricing therefore still reflects
        // the default product campaign, so the voucher discount has to be re-applied from the list price.
        const voucherCampaignAssociation = selectedOfferDetails.voucherInUse
          ? selectedOfferDetails.campaignAssociation
          : undefined;
        const voucherMonthlyDiscount = applicableSelfServicePricing
          ? (voucherCampaignAssociation?.monthlyRecurringCharge ?? 0)
          : 0;
        const voucherOnetimeDiscount = applicableSelfServicePricing
          ? (voucherCampaignAssociation?.oneTimeCharge ?? 0)
          : 0;

        const monthlyPrice = resolveEffectivePrice({
          priceBeforeDiscounts: originalMonthlyPrice,
          voucherDiscount: voucherMonthlyDiscount,
          selfServicePrice: applicableSelfServicePricing?.monthlyRecurringCharge,
          commercialProductPrice: (prices.monthlyRecurringCharge ?? 0) + addonsRecurringPrice,
        });
        const effectiveOneTimePrice = resolveEffectivePrice({
          priceBeforeDiscounts: originalOneTimePrice ?? 0,
          voucherDiscount: voucherOnetimeDiscount,
          selfServicePrice: applicableSelfServicePricing?.oneTimeCharge,
          commercialProductPrice: oneTimeCharge,
        });

        // Offer data is used only for analytics
        validateOfferAndModel(selectedOfferDetails.offer, onlineModel);

        // Define the type explicitly, otherwise TS compiler property check might fail
        const selectedCardWithAnalytics: SubCardWithAnalyticsV4Props = {
          ...cardContent,
          onChangeOffer: (guid: string) => handleChangeOffer(guid, cardContent?.cardName),
          selectedOffer: currentCardSelectedOffer?.offerCode,
          selected: currentCardSelectedOffer?.offerCode === selectedOffer?.offerCode,
          offers: cardContent.offers?.filter(o => eligibleOfferCodes.includes(o.guid)),
          // Safe to use non-null assertion, since offer existence is validated in validateOfferAndModel function
          offer: selectedOfferDetails.offer,
          onlineModel: onlineModel!,
          voucher: selectedOfferDetails.voucher,
          voucherCode: selectedOfferDetails.voucher,
          oneTimePrice: effectiveOneTimePrice,
          originalOneTimePrice,
          monthlyPrice,
          originalMonthlyPrice,
          campaignAssociation,
          fixedTermMonths: campaignAssociation?.fixedTermPeriod,
        };
        acc.push(selectedCardWithAnalytics);
      }
      return acc;
    }, []);
};
